import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import find from "lodash-es/find";
import { Observable, of } from "rxjs";
import { catchError, concatMap, map, shareReplay, tap } from "rxjs/operators";

import { environment } from "@environments/environment";
import { convertExpiryDate } from "../../core/common/utils";
import { PaymentAPIResponse } from "../../modules/invoice/models/payment-api-response.model";
import {
  AccountConfigModel,
  CompanyInformationModel,
} from "../models/account-config.model";
import { ColorInterval } from "../models/color-interval.interface";
import { ColorList } from "../models/color-list.interface";
import { JobSiteModel } from "../models/job-site.model";
import { PaymentInfoModel } from "../models/payment-info.model";
import { UserModel } from "../models/user.model";

function getIntervalDateLabel(hours: number): string {
  if (hours < 72) {
    return `${hours} hours ago`;
  }

  return `${Math.floor(hours / 24)} days ago`;
}

function getIntervalDate(hours: number): Date {
  const date = new Date();
  date.setHours(date.getHours() - hours);

  return date;
}

function getDateComparator(
  minDate: Date,
  maxDate: Date,
): (value: Date) => boolean {
  return (value: Date) => value >= minDate && value < maxDate;
}

export function getUser(data: any): UserModel {
  return new UserModel({
    id: data.Id,
    email: data.Email,
    firstName: data.Firstname,
    lastName: data.Lastname,
    accessLevel: +data.AccessLevel,
    username: data.Username,
    hasPaymentProfile: !!data.PaymentProfileId,
    notificationOptOut: data.NotificationOptOut,
    paymentProfileId: data.PaymentProfileId,
    paymentProfileAccountId: data.PaymentProfileAccountId,
    allowAutomaticPayments: data.AllowAutomaticPayments,
    automaticChargeLimit: data.AutomaticChargeLimit,
    paymentProvider: data.PaymentProvider,
    customerId: data.CustomerId,
    inviteAccepted: data.InviteAccepted,
  });
}

export function createCompanyInfo(item: any): CompanyInformationModel {
  return new CompanyInformationModel({
    CompanyID: item?.AccountID || item.CompanyID,
    CompanyName: item.CompanyName,
    address: item.Address,
    city: item.City,
    state: item.State,
    zip: item.Zip,
    country: item.Country,
    phone: item.Phone,
    email: item.Email,
    website: item.Website,
    invoiceNote1: item.InvoiceNote1,
    invoiceNote2: item.InvoiceNote2,
    invoiceNote3: item.InvoiceNote3,
    portalLogo: item.PortalImageUri
      ? `${item.PortalImageUri}`
      : "/assets/images/illustration/default_logo.svg",
  });
}

export function createPaymentInfo(data: any): PaymentInfoModel {
  return new PaymentInfoModel({
    id: data.Id,
    accountType: data.AccountType,
    creditCard: {
      account: data.Account,
      expiry: data.Expiry,
      name: data.Name,
      last4Digits: data.Last4Digits,
    },
    address: {
      address: data.Address,
      city: data.City,
      state: data.State,
      country: data.Country,
      postalCode: data.PostalCode,
    },
    achPayment: {
      account: data.Account,
      name: data.Name,
      last4Digits: data.Last4Digits,
    },
  });
}

@Injectable({ providedIn: "root" })
export class AccountService {
  private config: Observable<AccountConfigModel>;
  private jobSites: Array<JobSiteModel>;
  private colorList: ColorList;
  private account: UserModel;
  private users: Array<UserModel>;

  constructor(private readonly httpClient: HttpClient) {}

  getConfig(): Observable<AccountConfigModel> {
    if (this.config) {
      return this.config;
    }

    return (this.config = this.httpClient.get<any>("account/config").pipe(
      shareReplay(),
      map((config) => {
        const accountConfig = new AccountConfigModel({
          id: config.Id,
          showComments: config.ShowComments,
          showGPS: config.ShowGPS,
          intervalLow: config.IntervalLow,
          intervalPivot: config.IntervalPivot,
          intervalHigh: config.IntervalHigh,
          showClockIn: config.ShowClockIn,
          showPhotos: config.ShowPhotos,
          showPhotoComments: config.ShowPhotoComments,
          showServices: config.ShowServices,
          showScheduledDates: config.ShowScheduledDates,
          enablePayments: config.EnablePayments,
          portalLogoUri: config.PortalLogoUri
            ? `${config.PortalLogoUri}`
            : "/assets/images/illustration/default_logo.svg",
          showActualDates: config.ShowActualDates,
          commentEditTimeout: config.CommentEditTimeout,
          cashOnlyInvoicing: config.CashOnlyInvoicing,
        });

        if (config.CompanyInformation) {
          accountConfig.companyInformation = createCompanyInfo(
            config.CompanyInformation,
          );
        }

        return accountConfig;
      }),
    ));
  }

  getJobSites(): Observable<Array<JobSiteModel>> {
    if (this.jobSites) {
      return of(this.jobSites);
    }

    return this.httpClient.get<Array<any>>("jobsites").pipe(
      map(
        (items) =>
          (this.jobSites = items.map(
            (item) =>
              new JobSiteModel({
                id: item.Id,
                jobName: item.JobName,
                jobAddress: item.JobAddress,
              }),
          )),
      ),
    );
  }

  getColorIntervals(): Observable<ColorList> {
    if (this.colorList) {
      return of(this.colorList);
    }

    return this.getConfig().pipe(
      map((config) => {
        const { intervalLow, intervalPivot, intervalHigh } = config;
        this.initColorList(intervalLow, intervalPivot, intervalHigh);

        return this.colorList;
      }),
    );
  }

  getIntervalByDate(date: Date): Observable<ColorInterval> {
    return this.getColorIntervals().pipe(
      map((colorList) =>
        find(colorList, (interval) => interval.comparator(date)),
      ),
    );
  }

  getAccount(): Observable<UserModel> {
    if (this.account) {
      return of(this.account);
    }

    return this.httpClient.get<any>("profile").pipe(
      map((data) => (this.account = getUser(data))),
      concatMap((profile) =>
        this.getUsers().pipe(
          map((users) => {
            profile.autoChargeDisabled = users.some(
              (user) => user.allowAutomaticPayments && user.id !== profile.id,
            );

            return profile;
          }),
        ),
      ),
    );
  }

  setAccount(data: any): void {
    this.resetAccount();
    this.account = getUser(data);
  }

  updateAccount(data: Partial<UserModel>): void {
    Object.assign(this.account, data);
  }

  resetAccount(): void {
    this.account = undefined;
    this.config = undefined;
    this.colorList = undefined;
    this.jobSites = undefined;
  }

  getPaymentInfo(): Observable<PaymentInfoModel> {
    if (!this.account.hasPaymentProfile) {
      return of(new PaymentInfoModel());
    }

    return this.httpClient.get<any>("profile/paymentinfo").pipe(
      map(createPaymentInfo),
      catchError(() => of(new PaymentInfoModel())),
    );
  }

  savePaymentInfo(data: PaymentInfoModel): Observable<PaymentAPIResponse> {
    const method = data.id ? "PUT" : "POST";

    return this.httpClient.request<PaymentAPIResponse>(
      method,
      "profile/paymentinfo",
      {
        body: {
          Account: !(data.accountType === "ECHK")
            ? data.creditCard.account
            : data.achPayment.account,
          Expiry: convertExpiryDate(data.creditCard.expiry),
          Name: !(data.accountType === "ECHK")
            ? data.creditCard.name
            : data.achPayment.name,
          Address: data.address.address,
          City: data.address.city,
          State: data.address.state,
          Country: data.address.country,
          PostalCode: data.address.postalCode,
          AccountType: data.accountType,
          TokenId: !(data.accountType === "ECHK")
            ? data.creditCard.tokenId
            : data.achPayment.tokenId,
        },
      },
    );
  }

  removePaymentInfo(): Observable<PaymentInfoModel> {
    return this.httpClient.delete<any>("profile/paymentinfo").pipe(
      map(() => {
        this.updateAccount({ hasPaymentProfile: false });

        return new PaymentInfoModel();
      }),
    );
  }

  changePassword(data: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }): Observable<void> {
    return this.httpClient.put<any>("account/changepassword", {
      Password: data.currentPassword,
      NewPassword: data.newPassword,
      ConfirmPassword: data.confirmPassword,
    });
  }

  updateProfile(profileData: UserModel): Observable<UserModel> {
    return this.httpClient.put<any>("profile", profileData).pipe(
      tap((data) => {
        this.setAccount(data);
      }),
    );
  }

  resetProfile(): void {
    this.account = undefined;
  }

  getUsers(): Observable<Array<UserModel>> {
    if (this.users) {
      return of(this.users);
    }

    return this.httpClient
      .get<any>("users")
      .pipe(map((items) => (this.users = items.map(getUser))));
  }

  getUsersForPublic(userprofile, estimate): Observable<Array<UserModel>> {
    if (this.users) {
      return of(this.users);
    }

    if (estimate !== undefined) {
      return this.httpClient
        .post<any>("users/forpublic", { user: userprofile, estimate })
        .pipe(map((items) => (this.users = items.map(getUser))));
    }
    return of(null);
  }

  getIsWebProposalFeatureEnabled(): Observable<boolean> {
    return this.httpClient.get<boolean>(
      `${environment.AZURE_API_URL}/accountfeature/webproposals`,
    );
  }

  private initColorList(
    intervalLow: number,
    intervalPivot: number,
    intervalHigh: number,
  ): void {
    this.colorList = {
      inProgress: {
        color: "#43632E",
        name: "In Progress",
        colorId: 1,
        comparator: (value: Date) => !value,
      },
      neverVisited: {
        color: "#8F929F",
        name: "Never visited",
        colorId: 6,
        comparator: (value: Date) => {
          if (value) {
            // -8640000000000000 - special value to compare, when there is no visit in jobsite
            return value.getTime() === -8640000000000000;
          }
        },
      },
      low: {
        color: "#7CAF42",
        name: `Less than ${getIntervalDateLabel(intervalLow)}`,
        colorId: 2,
        comparator: getDateComparator(getIntervalDate(intervalLow), new Date()),
      },
      pivot: {
        color: "#F3A220",
        name: `Less than ${getIntervalDateLabel(intervalPivot)}`,
        colorId: 3,
        comparator: getDateComparator(
          getIntervalDate(intervalPivot),
          getIntervalDate(intervalLow),
        ),
      },
      high: {
        color: "#B23925",
        name: `More than ${getIntervalDateLabel(intervalPivot)}`,
        colorId: 4,
        comparator: getDateComparator(
          getIntervalDate(intervalHigh),
          getIntervalDate(intervalPivot),
        ),
      },
      moreThanHigh: {
        color: "#31444D",
        name: `More than ${getIntervalDateLabel(intervalHigh)}`,
        colorId: 5,
        comparator: getDateComparator(
          new Date(-8630000000000000),
          getIntervalDate(intervalHigh),
        ),
      },
    };
  }
}
